/**
 * LE point de lecture du drapeau `MANUEL_VIVANT` dans la console — les deux
 * étages réunis (chantier « manuel vivant », MV-SOC-01).
 *
 * ─── Premier étage : le build ───────────────────────────────────────────────
 *
 * `VITE_MANUEL_VIVANT=1`, posée dans l'étape de build de la console du
 * pipeline, à côté de `VITE_API_URL`. Comme elle, elle est remplacée par sa
 * valeur littérale AU BUILD : la changer demande un déploiement. Absente, la
 * console est exactement celle d'avant le chantier.
 *
 * C'est le SEUL module de la console qui lit cette variable. Jumeau de
 * `apps/mobile/src/features/manuel-vivant/drapeau.ts`.
 *
 * ─── Second étage : la capacité ─────────────────────────────────────────────
 *
 * Ce que le serveur accorde au compte connecté (`capacites.manuelVivant`),
 * résolu par organisation ou par foyer, rendu à la connexion et à chaque
 * renouvellement de jeton. La console rétablit sa session par un
 * renouvellement à chaque chargement de page (`AuthContext`) : la capacité est
 * donc relue à chaque fois, et n'a pas besoin d'être persistée.
 *
 * ─── Qui lit quoi ───────────────────────────────────────────────────────────
 *
 *  - Un écran, un onglet ou une entrée de menu DU CHANTIER : `manuelVivantActif()`.
 *  - Le RÉGLAGE de la capacité dans l'onglet d'administration :
 *    `MANUEL_VIVANT` seul. Il ne peut pas dépendre de la capacité qu'il sert à
 *    allumer — l'exploitant n'aurait aucun moyen de commencer.
 */
import { manuelVivantActif as regleManuelVivant } from '@silex/shared';

import { tokenStore } from '../api/tokens';

/** Le build de la console embarque-t-il le chantier ? Lu une fois. */
export const MANUEL_VIVANT: boolean = import.meta.env.VITE_MANUEL_VIVANT === '1';

/** Build à 1 ET capacité vraie pour le compte connecté. */
export function manuelVivantActif(): boolean {
  return regleManuelVivant(MANUEL_VIVANT, tokenStore.getCapacites());
}

/**
 * L'ANCIEN PARCOURS EST EN PAUSE DANS L'INTERFACE — décision F-32 du fondateur
 * (8 octobre 2026), Φ-MV-7 : « mettre en pause certaines options V2 dans le UX
 * pour permettre à l'expérience du produit d'être seamless et intuitive de
 * bout en bout […] ; après tous les tests end-to-end avec des utilisateurs, on
 * décidera ce qu'on en fait. »
 *
 * UN SEUL RÉGLAGE, RÉVERSIBLE : remettre cette constante à `false` rend au
 * tuteur la console d'avant, à l'identique, sans autre changement.
 *
 * En pause veut dire RETIRÉ DE LA NAVIGATION, DES BOUTONS ET DES LIENS — rien
 * n'est supprimé : les routes, les pages et leur code restent, et chaque page
 * s'ouvre encore par son adresse. Ce que la pause retire, pour un tuteur dont
 * la capacité « manuel vivant » est allumée :
 *
 *  - quatre entrées du rail (`components/Shell.tsx`, `ancienParcours`) :
 *    « Mes classes », « À valider », « Exercices », « Banque de savoirs » ;
 *  - l'ancienne fiche de suivi d'un élève (`/eleves/:id`) : la fiche du manuel
 *    EST la fiche de l'élève, et plus rien n'y renvoie ;
 *  - dans « Ma journée », les unités de l'ancien suivi que le manuel redit
 *    (`lib/ma-journee-manuel.ts`), le décompte des écartés, le critère, la
 *    tuile de la semaine.
 *
 * Elle ne joue QUE capacité allumée : un tuteur dont la capacité est éteinte
 * n'a que l'ancien parcours, on ne le lui retire pas.
 */
export const ANCIEN_PARCOURS_EN_PAUSE: boolean = true;

/** Le SEUL point de lecture du réglage : en pause, et seulement pour un compte dont la capacité est allumée. */
export function ancienParcoursEnPause(): boolean {
  return ANCIEN_PARCOURS_EN_PAUSE && manuelVivantActif();
}
